import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export const TIER_PICKER_IDS = {
  dialog: "tier-picker-dialog",
  base: "tier-base-option",
  unlimited: "tier-unlimited-option",
  continueBase: "tier-continue-base-button",
  continueUnlimited: "tier-continue-unlimited-button",
};

const fmt = (c) => `$${(c / 100).toFixed(2)} CAD`;

export default function TierPickerDialog({ open, onOpenChange, onPicked }) {
  const [tiers, setTiers] = useState(null);
  const [busyTier, setBusyTier] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/config").then(({ data }) => setTiers(data.tiers));
  }, [open]);

  const choose = async (tier) => {
    setBusyTier(tier);
    try {
      await onPicked(tier);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusyTier(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TIER_PICKER_IDS.dialog}
        className="bg-[#12141c] border-[#222634] text-slate-100 max-w-xl"
      >
        <DialogHeader>
          <p className="telesis-eyebrow">Pick a tier</p>
          <DialogTitle className="text-xl font-bold mt-1">
            How much mentation per month?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            An active membership (any tier) is required to receive monthly redistribution payouts.
          </DialogDescription>
        </DialogHeader>
        {!tiers && <p className="text-xs font-mono text-slate-500 mt-3">loading tiers…</p>}
        {tiers && (
          <div className="grid md:grid-cols-2 gap-3 mt-2">
            {tiers.map((t) => {
              const isUnlim = t.id === "unlimited";
              return (
                <div
                  key={t.id}
                  data-testid={isUnlim ? TIER_PICKER_IDS.unlimited : TIER_PICKER_IDS.base}
                  className={`rounded p-5 border ${
                    isUnlim ? "border-yellow-400/60 bg-yellow-400/5" : "border-[#2a2f3f] bg-black/30"
                  }`}
                >
                  <p className="telesis-eyebrow">{t.label}</p>
                  <p className={`text-3xl font-black mt-2 ${isUnlim ? "text-yellow-400" : "text-slate-100"}`}>
                    {fmt(t.amount_cents)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">per month</p>
                  <p className="mt-3 text-sm text-slate-200">
                    {t.votes_per_month === null ? "Unlimited" : t.votes_per_month} votes / month
                  </p>
                  <p className="mt-1 text-xs text-slate-400">{t.description}</p>
                  <Button
                    data-testid={isUnlim ? TIER_PICKER_IDS.continueUnlimited : TIER_PICKER_IDS.continueBase}
                    onClick={() => choose(t.id)}
                    disabled={busyTier !== null}
                    className={`w-full mt-4 rounded-full font-semibold ${
                      isUnlim ? "bg-yellow-400 text-black hover:bg-yellow-300" : "bg-slate-100 text-black hover:bg-white"
                    }`}
                  >
                    {busyTier === t.id ? "…" : `Continue · ${fmt(t.amount_cents)}`}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
